import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Copy, Download, MoreHorizontal, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CaptureRecord } from './types';
import { useToast } from '@/hooks/use-toast';

interface ReportsRowActionsProps {
  record: CaptureRecord;
  onExportRow: (record: CaptureRecord) => void;
  onDelete?: (record: CaptureRecord) => void;
}

export const ReportsRowActions: React.FC<ReportsRowActionsProps> = ({
  record,
  onExportRow,
  onDelete,
}) => {
  useLanguage();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(record.email);
    toast({
      title: translateInterfaceText("E-mail copiado!"),
      description: record.email,
    });
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText(record.phone);
    toast({
      title: translateInterfaceText("Telefone copiado!"),
      description: record.phone,
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">{translateInterfaceText("Abrir menu")}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleCopyEmail}>
            <Copy className="mr-2 h-4 w-4" />
            {translateInterfaceText("Copiar e-mail")}</DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyPhone}>
            <Copy className="mr-2 h-4 w-4" />
            {translateInterfaceText("Copiar telefone")}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onExportRow(record)}>
            <Download className="mr-2 h-4 w-4" />
            {translateInterfaceText("Exportar CSV (linha)")}</DropdownMenuItem>
          {onDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowDeleteDialog(true)}
                className="text-red-600 data-[highlighted]:bg-red-600 data-[highlighted]:text-white cursor-pointer"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {translateInterfaceText("Excluir")}</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Confirmar exclusão")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("Tem certeza que deseja excluir o registro de")}{" "}<strong>{record.name}</strong>{translateInterfaceText("? Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{translateInterfaceText("Cancelar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete?.(record);
                setShowDeleteDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {translateInterfaceText("Excluir")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
